/**
 * ANA SAYFA — koyu tema, Tailwind ile (2026-08-29)
 *
 * Eski sayfa `src/LandingPage.tsx` DURUYOR; rota buraya çevrildi, beğenilmezse
 * tek satırla geri alınır.
 *
 * Tasarım yönü (kullanıcı kararı): koyu tema, "online teknik servis + cari program"
 * ruhu — ciddi iş yazılımı. Neon/pastel yok; koyu lacivert zemin, ölçülü mavi vurgu,
 * emoji yerine çizgi ikonlar, rakamların hizalandığı tablo düzeni.
 *
 * 🔑 Tailwind burada preflight KAPALI çalışır (bkz. tailwind.config.js). Yani global
 *    sıfırlama yok; bu sayfanın kendi sıfırlaması aşağıdaki `sayfa` sarmalayıcısında
 *    elle veriliyor (kutu modeli, başlık margin'leri, liste işaretleri).
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Menu, X, ArrowRight, Check, ShieldCheck, Clock, Headphones, Wrench,
} from 'lucide-react';
import { subscriptionAPI, getAuthToken } from '../../services/apiClient';
import SEO from '../../components/SEO';
import { cn } from '../../lib/utils';
import { OZELLIKLER, RAKAMLAR } from './veri';
import UygulamaGorunumu from './UygulamaGorunumu';
import { SurecBolumu, KarsilastirmaBolumu, SssBolumu } from './bolumler';
import '../../styles/tw.css';

interface SitePlan {
  id: string;
  price: number;
  name: string;
  description: string;
  duration: string;
  features: string[];
  isActive: boolean;
}

const VARSAYILAN_OZELLIKLER = [
  'Sınırsız kullanıcı',
  'Sınırsız müşteri ve servis kaydı',
  'Müşteri Yönetimi',
  'Teknik Servis Takibi',
  'İş Emirleri Yönetimi',
  'Stok Yönetimi',
  'Kasa Yönetimi',
  'Fatura ve İşlem Takibi',
  'Personel Yönetimi',
  'Kapsamlı Raporlama',
  'SMS Entegrasyonu',
  'Mobil Erişim',
];

/** Sunucudan plan gelmezse gösterilecek liste — mevcut sayfadaki değerlerle aynı. */
const YEDEK_PLANLAR: SitePlan[] = [
  {
    id: 'monthly-plan', name: 'Aylık Plan', description: 'Esnek kullanım için ideal',
    price: 349, duration: 'monthly', features: VARSAYILAN_OZELLIKLER, isActive: true,
  },
  {
    id: 'yearly-plan', name: 'Yıllık Plan', description: 'En çok tercih edilen plan — %14 tasarruf',
    price: 299, duration: 'yearly', features: VARSAYILAN_OZELLIKLER, isActive: true,
  },
];

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [menuAcik, setMenuAcik] = useState(false);
  const [kaydirildi, setKaydirildi] = useState(false);
  const [planlar, setPlanlar] = useState<SitePlan[]>([]);

  // Oturumu acik olani tanitim sayfasinda bekletme, dogrudan panele al.
  // PWA kisayolu ve adres cubuguna "tservis.tr" yazan kullanici her acilista
  // yeniden giris yapiyormus gibi hissediyordu.
  // Tanitim sayfasini bilerek gormek icin: /?tanitim
  useEffect(() => {
    try {
      if (new URLSearchParams(window.location.search).has('tanitim')) return;
      if (getAuthToken()) navigate('/dashboard', { replace: true });
    } catch { /* ozel sekme: localStorage kapali olabilir */ }
  }, [navigate]);

  useEffect(() => {
    const kaydirma = () => setKaydirildi(window.scrollY > 12);
    window.addEventListener('scroll', kaydirma, { passive: true });
    planlariYukle();
    return () => window.removeEventListener('scroll', kaydirma);
  }, []);

  const planlariYukle = async (): Promise<void> => {
    try {
      const cevap = await subscriptionAPI.getActivePlans();
      const gelen = cevap.data?.data || cevap.data || [];

      if (Array.isArray(gelen) && gelen.length > 0) {
        const cevrilmis: SitePlan[] = gelen.map((p: any) => {
          const donem = p.billing_period === 'yearly' ? 'yearly' : 'monthly';
          const fiyat = Number(p.price) || 0;
          // Yıllık planda aylık eşdeğer fiyat gösterilir (karşılaştırma kolay olsun).
          return {
            id: p.id || `${donem}-plan`,
            name: p.plan_name || (donem === 'monthly' ? 'Aylık Plan' : 'Yıllık Plan'),
            description: p.description || (donem === 'monthly' ? 'Esnek kullanım için ideal' : 'En çok tercih edilen plan'),
            price: donem === 'yearly' ? Math.round(fiyat / 12) : fiyat,
            duration: donem,
            features: VARSAYILAN_OZELLIKLER,
            isActive: true,
          };
        }).sort((a: SitePlan) => (a.duration === 'monthly' ? -1 : 1));
        setPlanlar(cevrilmis);
      } else {
        setPlanlar(YEDEK_PLANLAR);
      }
    } catch {
      // Sunucuya ulaşılamazsa sayfa boş kalmasın.
      setPlanlar(YEDEK_PLANLAR);
    }
  };

  const bolumeGit = (id: string): void => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMenuAcik(false);
  };

  const menuOgeleri = [
    { id: 'ozellikler', ad: 'Özellikler' },
    { id: 'fiyatlar', ad: 'Fiyatlandırma' },
  ];

  return (
    <>
      <SEO
        title="Teknik Servis Online Program - tservis | CRM Online | Profesyonel Servis Takip"
        description="Teknik servis online program - tservis. Teknik servis işletmeniz için kapsamlı yönetim sistemi ve CRM online çözümü. Servis takibi, müşteri yönetimi, stok kontrolü, raporlama ve daha fazlası. Ücretsiz deneme ile başlayın."
        keywords="teknik servis online, teknikservis, teknikservis online program, crm online, tservis, teknik servis yönetim sistemi, servis takip programı, müşteri yönetimi, stok yönetimi, servis kayıt sistemi, teknik servis yazılımı, servis takip, müşteri takip, raporlama sistemi"
        url="https://tservis.tr/landing"
        canonical="https://tservis.tr/landing"
        type="website"
      />

      {/* Sayfa sarmalayıcısı: preflight kapalı olduğu için gerekli sıfırlama burada.
          `[&_*]:box-border` ile kutu modeli bu ağaçta tutarlı hale getiriliyor. */}
      <div className="tw-alan min-h-screen bg-zemin-900 font-sans text-slate-700 antialiased [&_*]:box-border">

        {/* ══════════════ ÜST MENÜ ══════════════ */}
        <header
          className={cn(
            'fixed inset-x-0 top-0 z-50 transition-colors duration-300',
            kaydirildi
              ? 'border-b border-slate-200 bg-white/90 backdrop-blur-md'
              : 'border-b border-transparent',
          )}
        >
          <nav className="mx-auto flex h-16 max-w-6xl items-center gap-4 px-5">
            <a href="/" className="flex items-center gap-2.5 no-underline">
              <span className="grid h-9 w-9 place-items-center rounded-lg bg-vurgu-500/15 ring-1 ring-vurgu-500/30">
                <Wrench className="h-[18px] w-[18px] text-vurgu-400" />
              </span>
              <span className="text-[17px] font-bold tracking-tight text-slate-900">tservis</span>
            </a>

            <div className="ml-auto hidden items-center gap-1 md:flex">
              {menuOgeleri.map((m) => (
                <button
                  key={m.id}
                  onClick={() => bolumeGit(m.id)}
                  className="rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
                >
                  {m.ad}
                </button>
              ))}
              <button
                onClick={() => navigate('/signin')}
                className="ml-2 rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
              >
                Giriş Yap
              </button>
              <button
                onClick={() => navigate('/register')}
                className="ml-1 inline-flex items-center gap-1.5 rounded-lg bg-vurgu-500 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-vurgu-600/25 transition-colors hover:bg-vurgu-600"
              >
                Ücretsiz Dene <ArrowRight className="h-4 w-4" />
              </button>
            </div>

            <button
              onClick={() => setMenuAcik(!menuAcik)}
              aria-label={menuAcik ? 'Menüyü kapat' : 'Menüyü aç'}
              aria-expanded={menuAcik}
              className="ml-auto grid h-10 w-10 place-items-center rounded-lg text-slate-700 hover:bg-slate-100 md:hidden"
            >
              {menuAcik ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </nav>

          {menuAcik && (
            <div className="border-t border-slate-200 bg-white/95 px-5 py-3 backdrop-blur-md md:hidden">
              <div className="flex flex-col gap-1">
                {menuOgeleri.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => bolumeGit(m.id)}
                    className="rounded-lg px-3 py-2.5 text-left text-sm font-medium text-slate-700 hover:bg-slate-100"
                  >
                    {m.ad}
                  </button>
                ))}
                <button
                  onClick={() => navigate('/signin')}
                  className="rounded-lg px-3 py-2.5 text-left text-sm font-medium text-slate-700 hover:bg-slate-100"
                >
                  Giriş Yap
                </button>
                <button
                  onClick={() => navigate('/register')}
                  className="mt-1 rounded-lg bg-vurgu-500 px-4 py-2.5 text-sm font-semibold text-white"
                >
                  Ücretsiz Dene
                </button>
              </div>
            </div>
          )}
        </header>

        {/* ══════════════ HERO ══════════════ */}
        <section className="relative overflow-hidden pt-24 pb-8 md:pt-28 md:pb-10">
          {/* Arkadaki ışıma — dekoratif, ekran okuyucudan gizli */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute -left-[10%] -top-[20%] h-[560px] w-[560px] rounded-full bg-vurgu-500/[0.10] blur-[120px] motion-safe:animate-isima-kay" />
            <div className="absolute -right-[12%] top-[10%] h-[520px] w-[520px] rounded-full bg-indigo-500/[0.07] blur-[130px]" />
            {/* İnce ızgara — teknik/mühendislik hissi */}
            <div
              className="absolute inset-0 opacity-[0.55]"
              style={{
                backgroundImage:
                  'linear-gradient(rgba(148,163,184,.35) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,.35) 1px, transparent 1px)',
                backgroundSize: '56px 56px',
                maskImage: 'radial-gradient(ellipse 70% 55% at 50% 40%, #000 55%, transparent 100%)',
                WebkitMaskImage: 'radial-gradient(ellipse 70% 55% at 50% 40%, #000 55%, transparent 100%)',
              }}
            />
          </div>

          <div className="mx-auto max-w-6xl px-5">
            {/* Üst blok: ortalanmış başlık — ürün görseline yer açmak için tek sütun */}
            <div className="mx-auto max-w-4xl text-center motion-safe:animate-yukari-bel">
              <button
                onClick={() => bolumeGit('ozellikler')}
                className="mb-5 inline-flex items-center gap-2 rounded-full border border-zemin-600 bg-white py-1.5 pl-2.5 pr-3.5 text-[13px] font-medium text-slate-600 backdrop-blur transition-colors hover:border-vurgu-500/50 hover:text-slate-900"
              >
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-olumlu opacity-70 motion-safe:animate-ping" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-olumlu" />
                </span>
                Yeni: WhatsApp asistanı ile otomatik müşteri bildirimi
                <ArrowRight className="h-3.5 w-3.5 opacity-60" />
              </button>

              <h1 className="text-[1.9rem] font-extrabold leading-[1.08] tracking-[-0.02em] text-slate-900 sm:text-[2.15rem] md:text-[2.6rem] lg:text-[3.25rem]">
                <span className="block">Teknik servis ve cari hesabınız</span>
                <span className="block bg-gradient-to-r from-vurgu-600 via-vurgu-500 to-indigo-500 bg-clip-text text-transparent">
                  tek panelde
                </span>
              </h1>

              <p className="mx-auto mt-4 max-w-[46rem] text-pretty text-[16.5px] leading-relaxed text-slate-600 md:text-[18px]">
                Cihaz kabulünden teslimata, stok ve kasadan cari ekstreye kadar tüm
                operasyonunuzu tek yerden yönetin. Kurulum gerekmez, tarayıcıdan çalışır.
              </p>

              <div className="mt-7 flex flex-wrap justify-center gap-3">
                <button
                  onClick={() => navigate('/register')}
                  className="inline-flex items-center gap-2 rounded-xl bg-vurgu-500 px-7 py-3.5 text-[15px] font-semibold text-white shadow-xl shadow-vurgu-600/30 transition-all hover:bg-vurgu-600 hover:shadow-vurgu-600/40 motion-safe:hover:-translate-y-0.5"
                >
                  14 Gün Ücretsiz Deneyin <ArrowRight className="h-4 w-4" />
                </button>
                <button
                  onClick={() => bolumeGit('fiyatlar')}
                  className="rounded-xl border border-slate-300 bg-white px-7 py-3.5 text-[15px] font-semibold text-slate-700 shadow-sm transition-colors hover:border-slate-400 hover:bg-slate-50"
                >
                  Fiyatları İncele
                </button>
              </div>

              <p className="mt-5 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[13px] text-slate-500">
                <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-olumlu" /> Kredi kartı gerekmez</span>
                <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4 text-olumlu" /> 5 dakikada kurulum</span>
                <span className="inline-flex items-center gap-1.5"><Headphones className="h-4 w-4 text-olumlu" /> 7/24 destek</span>
              </p>
            </div>

            {/* ÜRÜN GÖRSELİ — sayfanın merkezinde, sekmeli */}
            <div className="mt-11 motion-safe:animate-yukari-bel">
              <UygulamaGorunumu />
            </div>

            {/* Rakamlar */}
            <dl className="mx-auto mt-10 grid max-w-3xl grid-cols-3 gap-px overflow-hidden rounded-2xl border border-zemin-600 bg-zemin-600">
              {RAKAMLAR.map((r) => (
                <div key={r.etiket} className="bg-zemin-800 px-4 py-5 text-center">
                  <dd className="text-2xl font-bold tabular-nums text-slate-900 md:text-3xl">{r.deger}</dd>
                  <dt className="mt-1 text-[11px] uppercase tracking-wider text-slate-500">{r.etiket}</dt>
                </div>
              ))}
            </dl>
          </div>
        </section>

        <SurecBolumu />

        {/* ══════════════ ÖZELLİKLER ══════════════ */}
        <section id="ozellikler" className="scroll-mt-20 border-t border-slate-200 bg-slate-50/70 py-12 md:py-16">
          <div className="mx-auto max-w-6xl px-5">
            <div className="max-w-2xl">
              <span className="text-xs font-semibold uppercase tracking-[0.14em] text-vurgu-600">
                Neler yapabilirsiniz
              </span>
              <h2 className="mt-3 text-balance text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">
                İşletmenizin tamamı, tek yazılımda
              </h2>
              <p className="mt-4 text-[16px] leading-relaxed text-slate-600">
                Ayrı ayrı program kullanmaya son. Servis, cari, stok, kasa ve personel
                aynı veritabanında çalışır — bir yerde girdiğiniz kayıt diğerine anında yansır.
              </p>
            </div>

            <div className="mt-9 grid gap-px overflow-hidden rounded-2xl border border-zemin-600 bg-zemin-600 sm:grid-cols-2 lg:grid-cols-3">
              {OZELLIKLER.map(({ ikon: Ikon, baslik, aciklama }) => (
                <div
                  key={baslik}
                  className="group bg-zemin-800 p-5 transition-colors hover:bg-slate-100"
                >
                  <span className="grid h-10 w-10 place-items-center rounded-lg bg-vurgu-500/10 ring-1 ring-vurgu-500/20 transition-colors group-hover:bg-vurgu-500/20">
                    <Ikon className="h-5 w-5 text-vurgu-400" />
                  </span>
                  <h3 className="mt-4 text-[17px] font-semibold tracking-tight text-slate-900">{baslik}</h3>
                  <p className="mt-2 text-[14px] leading-relaxed text-slate-600">{aciklama}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <KarsilastirmaBolumu />

        {/* ══════════════ FİYATLANDIRMA ══════════════ */}
        <section id="fiyatlar" className="scroll-mt-20 border-t border-slate-200 py-12 md:py-16">
          <div className="mx-auto max-w-6xl px-5">
            <div className="mx-auto max-w-2xl text-center">
              <span className="text-xs font-semibold uppercase tracking-[0.14em] text-vurgu-600">
                Fiyatlandırma
              </span>
              <h2 className="mt-3 text-balance text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">
                Size uygun planı seçin
              </h2>
              <p className="mt-4 text-[16px] text-slate-600">
                Tüm planlarda tüm özellikler açık. Kullanıcı ve kayıt sınırı yok.
              </p>
            </div>

            <div className="mx-auto mt-9 grid max-w-4xl gap-6 md:grid-cols-2">
              {planlar.map((plan) => {
                const yillik = plan.duration === 'yearly';
                return (
                  <div
                    key={plan.id}
                    className={cn(
                      'relative flex flex-col rounded-2xl border p-6',
                      yillik
                        ? 'border-vurgu-500/50 bg-gradient-to-b from-vurgu-500/[0.09] to-zemin-800 shadow-xl shadow-vurgu-600/10'
                        : 'border-zemin-600 bg-zemin-800',
                    )}
                  >
                    {yillik && (
                      <span className="absolute -top-3 left-7 rounded-full bg-vurgu-500 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
                        %14 tasarruf
                      </span>
                    )}

                    <h3 className="text-lg font-bold text-slate-900">{plan.name}</h3>
                    <p className="mt-1 text-sm text-slate-600">{plan.description}</p>

                    <div className="mt-5 flex items-baseline gap-1.5">
                      <span className="text-4xl font-extrabold tabular-nums text-slate-900">₺{plan.price}</span>
                      <span className="text-sm text-slate-500">
                        {yillik ? '/ ay (yıllık ödemede)' : '/ ay'}
                      </span>
                    </div>

                    <ul className="mt-7 flex-1 space-y-2.5 p-0">
                      {plan.features.map((o) => (
                        <li key={o} className="flex items-start gap-2.5 text-sm text-slate-600">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-olumlu" />
                          {o}
                        </li>
                      ))}
                    </ul>

                    <button
                      onClick={() => navigate('/register')}
                      className={cn(
                        'mt-7 w-full rounded-xl px-5 py-3.5 text-[15px] font-semibold transition-colors',
                        yillik
                          ? 'bg-vurgu-500 text-white shadow-lg shadow-vurgu-600/25 hover:bg-vurgu-600'
                          : 'border border-zemin-600 bg-slate-100 text-slate-100 hover:border-slate-300 hover:bg-slate-100',
                      )}
                    >
                      14 Gün Ücretsiz Dene
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <SssBolumu />

        {/* ══════════════ SON ÇAĞRI ══════════════ */}
        <section className="border-t border-slate-200 px-5 py-12 md:py-16">
          <div className="relative mx-auto max-w-5xl overflow-hidden rounded-3xl bg-gradient-to-br from-vurgu-600 to-indigo-700 px-6 py-14 text-center shadow-2xl shadow-vurgu-600/20 md:px-16">
            <div aria-hidden className="pointer-events-none absolute -right-16 -top-24 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
            <div className="relative">
              <h2 className="text-balance text-3xl font-bold tracking-tight text-white md:text-[2.5rem]">
                Hazır mısınız?
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-[16px] leading-relaxed text-blue-100">
                Kayıt olun, 14 gün boyunca tüm özellikleri ücretsiz kullanın.
                Beğenmezseniz tek tıkla iptal edin.
              </p>
              <button
                onClick={() => navigate('/register')}
                className="mt-8 inline-flex items-center gap-2 rounded-xl bg-white px-8 py-4 text-[15px] font-semibold text-vurgu-600 shadow-xl shadow-black/10 transition-all hover:bg-blue-50 motion-safe:hover:-translate-y-0.5"
              >
                Hemen Başlayın <ArrowRight className="h-4 w-4" />
              </button>
              <p className="mt-5 text-[13px] text-blue-200">Kredi kartı gerekmez · Taahhüt yok</p>
            </div>
          </div>
        </section>

        {/* ══════════════ ALT BİLGİ ══════════════ */}
        <footer className="border-t border-slate-200 py-8">
          <div className="mx-auto flex max-w-6xl flex-col items-center gap-4 px-5 text-center sm:flex-row sm:justify-between sm:text-left">
            <div className="flex items-center gap-2.5">
              <span className="grid h-8 w-8 place-items-center rounded-lg bg-vurgu-500/15 ring-1 ring-vurgu-500/30">
                <Wrench className="h-4 w-4 text-vurgu-400" />
              </span>
              <span className="text-sm font-semibold text-slate-900">tservis</span>
            </div>
            <p className="text-[13px] text-slate-500">
              © {new Date().getFullYear()} tservis — Teknik servis ve cari yönetim yazılımı
            </p>
            <a href="mailto:info@tservis.tr" className="text-[13px] text-slate-600 no-underline hover:text-slate-900">
              info@tservis.tr
            </a>
          </div>
        </footer>
      </div>
    </>
  );
};

export default LandingPage;
